import { TranslationKey } from '~/lib';

export enum BasketErrorType {
    BASKET_FULL = 'BASKET_FULL', // Maps from BasketQuotaExceededException
    NOT_FOUND = 'NOT_FOUND', // Maps from InvalidProductItemException
    SOLD_OUT = 'SOLD_OUT', // Maps from ProductItemNotAvailableException
    NETWORK_ERROR = 'NETWORK_ERROR', // For HTTP/network failures
    INVALID_QUANTITY = 'INVALID_QUANTITY', // For quantity validation errors
    UNKNOWN = 'UNKNOWN', // For unhandled errors
}

export enum ApiErrorType {
    SOLD_OUT = 'ProductItemNotAvailableException',
    NOT_FOUND = 'InvalidProductItemException',
    BASKET_FULL = 'BasketQuotaExceededException',
}

export interface ErrorResponse {
    type: BasketErrorType;
    message: string;
    retryable: boolean;
    statusCode?: number;
    details?: Record<string, unknown>;
}

export const isErrorResponse = (error: unknown): error is ErrorResponse => {
    const err = error as Partial<ErrorResponse>;
    return !!err && typeof err.type === 'string' && typeof err.message === 'string';
};

export const RETRY_CONFIG = {
    MAX_RETRIES: 3,
    INITIAL_DELAY: 1000, // 1 second
    MAX_DELAY: 5000, // 5 seconds
} as const;

/**
 * Queue for managing concurrent basket operations
 */
export class BasketOperationQueue {
    private static instance: BasketOperationQueue;
    private queue: Array<() => Promise<void>> = [];
    private isProcessing = false;

    private constructor() {
        // Private constructor to enforce singleton
    }

    public static getInstance(): BasketOperationQueue {
        if (!BasketOperationQueue.instance) {
            BasketOperationQueue.instance = new BasketOperationQueue();
        }
        return BasketOperationQueue.instance;
    }

    async add(operation: () => Promise<void>) {
        return new Promise<void>((resolve, reject) => {
            this.queue.push(async () => {
                try {
                    await operation();
                    resolve();
                } catch (error) {
                    reject(error);
                }
            });
            this.processQueue();
        });
    }

    private async processQueue() {
        if (this.isProcessing || this.queue.length === 0) return;

        this.isProcessing = true;
        while (this.queue.length > 0) {
            const operation = this.queue.shift();
            if (operation) {
                try {
                    await operation();
                } catch (error) {
                    console.error('Error processing basket operation:', error);
                }
            }
        }
        this.isProcessing = false;
    }
}

export type BasketOperation<T, Args extends any[] = any[]> = (...args: Args) => Promise<T>;

/**
 * Creates a retry-enabled error handler for basket operations
 */
const DEFAULT_TIMEOUT = 5000; // 5 seconds

const withTimeout = async <T>(operation: () => Promise<T>): Promise<T> => {
    return Promise.race([
        operation(),
        new Promise((_, reject) =>
            setTimeout(
                () =>
                    reject({
                        type: BasketErrorType.NETWORK_ERROR,
                        message: 'Operation timed out',
                        retryable: true,
                    }),
                DEFAULT_TIMEOUT,
            ),
        ),
    ]) as Promise<T>;
};

export const createBasketErrorHandler = (trackError: (error: unknown) => void) => {
    return <T, Args extends any[]>(fn: BasketOperation<T, Args>) => {
        const getRetryDelay = (attempt: number) => {
            const delay = Math.min(
                RETRY_CONFIG.INITIAL_DELAY * Math.pow(2, attempt),
                RETRY_CONFIG.MAX_DELAY,
            );
            return delay + Math.random() * 1000; // Add jitter
        };

        return async (...args: Args): Promise<T> => {
            let lastError: unknown;

            for (let attempt = 0; attempt < RETRY_CONFIG.MAX_RETRIES; attempt++) {
                try {
                    return await withTimeout(() => fn(...args));
                } catch (e: unknown) {
                    lastError = e;

                    // Don't retry if it's a business logic error
                    if (
                        isErrorResponse(e) &&
                        (e.type === BasketErrorType.BASKET_FULL ||
                            e.type === BasketErrorType.NOT_FOUND ||
                            e.type === BasketErrorType.SOLD_OUT)
                    ) {
                        throw e;
                    }

                    // Last attempt
                    if (attempt === RETRY_CONFIG.MAX_RETRIES - 1) {
                        trackError(e);
                        throw e;
                    }

                    // Wait before retrying
                    await new Promise((resolve) => setTimeout(resolve, getRetryDelay(attempt)));
                }
            }

            // This should never be reached due to the throw in the last attempt
            throw lastError;
        };
    };
};

/**
 * Maps API error types to our basket error types
 */
export const mapApiErrorToBasketError = (
    error: unknown,
    translate: (key: TranslationKey) => string,
): ErrorResponse => {
    if (!isErrorResponse(error)) {
        return {
            type: BasketErrorType.UNKNOWN,
            message: translate('basket.error.unknown'),
            retryable: true,
        };
    }

    const apiError = error.type as unknown as ApiErrorType;
    switch (apiError) {
        case ApiErrorType.BASKET_FULL:
            return {
                type: BasketErrorType.BASKET_FULL,
                message: translate('basket.basketIsFull'),
                retryable: false,
            };
        case ApiErrorType.NOT_FOUND:
            return {
                type: BasketErrorType.NOT_FOUND,
                message: translate('basket.itemNotFound'),
                retryable: false,
            };
        case ApiErrorType.SOLD_OUT:
            return {
                type: BasketErrorType.SOLD_OUT,
                message: translate('basket.itemSoldOut'),
                retryable: false,
            };
        default:
            return {
                type: BasketErrorType.UNKNOWN,
                message: translate('basket.addToBasketError'),
                retryable: true,
            };
    }
};
